// 0613 ADR-024 — hook + Context para acceso granular por modulo.
//
// Uso en componentes:
//   const nivel = useModuloAcceso('M1');
//   if (nivel === 'ADMIN') { ... }
//
// El Provider se monta en AppShell y carga UNA vez los accesos del usuario al
// mount. Los hooks consumen del context (no fetcha por hook). Cortocircuita
// para roles admin/auditor sin tocar BD.

import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { useAuth } from '../context/AuthContext';
import {
  fetchMyModuloAcceso,
  type ModuloAccesoRow,
  type NivelModuloAcceso,
} from '../modules/shared/services/api';

interface ModuloAccesoContextValue {
  // Mapa modulo -> nivel (solo MEMBERs tienen entradas; otros roles vacio)
  accesosMap: Record<string, NivelModuloAcceso>;
  // Rol base del usuario actual
  rol: string;
  loading: boolean;
  // Forzar reload (ej: tras admin cambiar accesos)
  refresh: () => void;
}

const ModuloAccesoContext = createContext<ModuloAccesoContextValue>({
  accesosMap: {},
  rol: '',
  loading: false,
  refresh: () => {},
});

const ADMIN_ROLES = new Set(['SUPERADMIN_PLATFORM', 'TENANT_OWNER', 'TENANT_ADMIN']);
const AUDITOR_ROLES = new Set(['TENANT_AUDITOR']);

export function ModuloAccesoProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const rol = user?.role ?? '';
  const userId = user?.userId ?? '';

  const [accesosMap, setAccesosMap] = useState<Record<string, NivelModuloAcceso>>({});
  const [loading, setLoading] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    // Solo MEMBERs hacen fetch. Otros roles tienen niveles implicitos.
    if (!userId) {
      setAccesosMap({});
      return;
    }
    if (ADMIN_ROLES.has(rol) || AUDITOR_ROLES.has(rol)) {
      setAccesosMap({});
      return;
    }
    if (rol !== 'MEMBER') {
      setAccesosMap({});
      return;
    }

    let cancelled = false;
    setLoading(true);
    fetchMyModuloAcceso()
      .then((res) => {
        if (cancelled) return;
        const map: Record<string, NivelModuloAcceso> = {};
        for (const row of res.data?.accesos ?? ([] as ModuloAccesoRow[])) {
          map[row.modulo] = row.nivel;
        }
        setAccesosMap(map);
      })
      .catch(() => {
        if (!cancelled) setAccesosMap({});
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [userId, rol, reloadKey]);

  const refresh = () => setReloadKey((k) => k + 1);

  // 0615e fix: refetch automatico cuando el admin cambia los accesos del
  // usuario desde otra sesion. Sin esto, el usuario afectado seguia con
  // cache antiguo en memoria hasta hacer F5 / re-login.
  //
  // Estrategia (defensa en profundidad):
  //   1) refetch on focus  - cuando el browser tab vuelve a primer plano
  //   2) polling 5 min     - fallback si el tab nunca pierde foco
  //
  // Solo aplica a MEMBER (los demas roles tienen niveles implicitos).
  useEffect(() => {
    if (rol !== 'MEMBER' || !userId) return;
    const onFocus = () => setReloadKey((k) => k + 1);
    window.addEventListener('focus', onFocus);
    const intervalId = window.setInterval(
      () => setReloadKey((k) => k + 1),
      5 * 60 * 1000, // 5 min
    );
    return () => {
      window.removeEventListener('focus', onFocus);
      window.clearInterval(intervalId);
    };
  }, [rol, userId]);

  const value = useMemo(() => ({ accesosMap, rol, loading, refresh }), [accesosMap, rol, loading]);

  return <ModuloAccesoContext.Provider value={value}>{children}</ModuloAccesoContext.Provider>;
}

/**
 * Devuelve el nivel efectivo del usuario actual en `modulo`.
 * Cortocircuita por rol base (ADR-023). MEMBER consume del Context.
 */
export function useModuloAcceso(modulo: string): NivelModuloAcceso {
  const { accesosMap, rol } = useContext(ModuloAccesoContext);

  if (ADMIN_ROLES.has(rol)) return 'ADMIN';
  if (AUDITOR_ROLES.has(rol)) return 'VIEWER';
  if (rol !== 'MEMBER') return 'VIEWER';

  return accesosMap[modulo] ?? 'VIEWER';
}

/**
 * Devuelve true si el usuario tiene al menos `nivelMinimo` en `modulo`.
 */
const RANK: Record<NivelModuloAcceso, number> = { VIEWER: 1, EDITOR: 2, ADMIN: 3 };
export function useHasNivelModulo(modulo: string, nivelMinimo: NivelModuloAcceso): boolean {
  const nivel = useModuloAcceso(modulo);
  return RANK[nivel] >= RANK[nivelMinimo];
}

/**
 * Hook crudo para acceder al refresh del Context (tras admin cambiar accesos).
 */
export function useModuloAccesoRefresh(): () => void {
  return useContext(ModuloAccesoContext).refresh;
}

/**
 * 0615e — Devuelve el mapa completo de accesos especiales del usuario actual
 * (modulo -> nivel) + el rol base. Util para listar los accesos en UI
 * (ej. menu avatar). Solo MEMBERs tienen entradas en el map; admins/auditor
 * tienen niveles implicitos por rol y devuelven {} (no se enumeran 18 modulos).
 */
export function useAccesosMap(): { accesosMap: Record<string, NivelModuloAcceso>; rol: string } {
  const { accesosMap, rol } = useContext(ModuloAccesoContext);
  return { accesosMap, rol };
}

/**
 * 0615c — Wrapper declarativo para gating en JSX.
 *
 * Uso:
 *   <RequireNivel modulo="M1" nivel="EDITOR">
 *     <button onClick={crearPersona}>Crear persona</button>
 *   </RequireNivel>
 *
 * Si el usuario no llega al nivel pedido, NO renderiza nada (default).
 * Pasar `fallback` para mostrar tooltip / version disabled / mensaje.
 *
 * Cortocircuita por rol base (ADR-023): SUPERADMIN/OWNER/TENANT_ADMIN siempre
 * pasan el gate (admin implicito). AUDITOR/MEMBER consultan la matriz.
 */
export function RequireNivel({
  modulo,
  nivel,
  children,
  fallback = null,
}: {
  modulo: string;
  nivel: NivelModuloAcceso;
  children: ReactNode;
  fallback?: ReactNode;
}) {
  const ok = useHasNivelModulo(modulo, nivel);
  return <>{ok ? children : fallback}</>;
}
